import React, { createContext, useContext, useState, useEffect } from 'react';

export const translations = {
  fr: {
    motto: "Votre vision, réalisée.",
    eyebrow: "Information de disponibilité",
    title: "Le service que vous cherchez rencontre un souci.",
    lede: "Ce service ne répond pas comme prévu. Consultez l'état ci-dessous. En cas d'urgence, appelez ou écrivez.",
    urgent: "Nous sommes désolés pour le contretemps.",
    call: "Appeler le 819 481 1022",
    email: "Ecrire à contact@udesignproductions.com",
    statusTitle: "Surveillance en direct",
    statusDesc: "Tests rapides vers nos services clés. Certains résultats peuvent afficher \"inconnu\" si un pare-feu ou CORS bloque la réponse.",
    refreshNow: "Actualiser",
    refreshing: "Actualisation...",
    autoLabel: "Auto (45s)",
    lastCheckedLabel: "Dernière vérification",
    autoHint: "Désactivez l'auto si vous préférez limiter les requêtes.",
    noChecks: "Aucun test configuré pour le moment.",
    statusLabels: {
      operational: "Opérationnel",
      degraded: "Incertitude",
      down: "Indisponible",
      unknown: "Inconnu / CORS",
      auth: "Opérationnel (Auth)"
    },
    detail: {
      ok: "Réponse OK",
      http: "HTTP",
      timeout: "Délai dépassé",
      network: "Réseau ou CORS bloqué",
      auth: "Authentification requise (Normal)"
    },
    adminTitle: "Administration",
    adminSubtitle: "Veuillez vous authentifier",
    loginBtn: "Connexion",
    goBack: "Retour",
    invalidKey: "Clé invalide.",
    dashboardDesc: "Gérez l'état du système et les raisons de la panne.",
    reasonMaintenance: "Maintenance planifiée",
    reasonUpgrade: "Mise à jour système",
    reasonOutage: "Panne inattendue",
    reasonCustom: "Message personnalisé",
    customMessageLabel: "Message personnalisé",
    logoutBtn: "Déconnexion",
    updateBtn: "Mettre à jour",
    accessKeyPlaceholder: "Clé d'accès...",
    customMessagePlaceholder: "Ex: Retour en ligne estimé à 14h00..."
  },
  en: {
    motto: "Your vision, realized.",
    eyebrow: "Availability notice",
    title: "The service you tried to reach is having trouble.",
    lede: "That service isn't responding as expected. Check live status below. If it's urgent, call or email.",
    urgent: "Sorry for the detour.",
    call: "Call 819 481 1022",
    email: "Email contact@udesignproductions.com",
    statusTitle: "Live status",
    statusDesc: "Quick pings to our key services. Some results may show \"unknown\" if a firewall or CORS blocks the response.",
    refreshNow: "Refresh",
    refreshing: "Refreshing...",
    autoLabel: "Auto (45s)",
    lastCheckedLabel: "Last check",
    autoHint: "Turn off auto if you want to reduce requests.",
    noChecks: "No checks configured yet.",
    statusLabels: {
      operational: "Operational",
      degraded: "Degraded",
      down: "Unavailable",
      unknown: "Unknown / CORS",
      auth: "Operational (Auth)"
    },
    detail: {
      ok: "Response OK",
      http: "HTTP",
      timeout: "Timed out",
      network: "Network or CORS blocked",
      auth: "Authentication required (Normal)"
    },
    adminTitle: "Administration",
    adminSubtitle: "Please authenticate",
    loginBtn: "Login",
    goBack: "Go Back",
    invalidKey: "Invalid key.",
    dashboardDesc: "Manage system status and outage reasons.",
    reasonMaintenance: "Planned maintenance",
    reasonUpgrade: "System upgrade",
    reasonOutage: "Unexpected outage",
    reasonCustom: "Custom message",
    customMessageLabel: "Custom message",
    logoutBtn: "Logout",
    updateBtn: "Update",
    accessKeyPlaceholder: "Access key...",
    customMessagePlaceholder: "Ex: Estimated return online at 2:00 PM..."
  }
} as const;

type Language = 'fr' | 'en';

interface LanguageContextType {
  lang: Language;
  setLang: (lang: Language) => void;
  t: (key: string) => string;
  statusLabels: Record<string, string>;
  detailLabels: Record<string, string>;
}

const LanguageContext = createContext<LanguageContextType | null>(null);

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [lang, setLang] = useState<Language>('fr');

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const t = (key: string): string => {
    const keys = key.split('.');
    let current: any = translations[lang];
    for (const k of keys) {
      if (current && typeof current === 'object' && k in current) {
        current = current[k];
      } else {
        return key;
      }
    }
    return typeof current === 'string' ? current : key;
  };

  const statusLabels = translations[lang].statusLabels;
  const detailLabels = translations[lang].detail;

  return (
    <LanguageContext.Provider value={{ lang, setLang, t, statusLabels, detailLabels }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
